import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Sidebar from '../components/Sidebar.tsx';
import { useAuth } from '../context/AuthContext.tsx';

const API_BASE = process.env.REACT_APP_API_URL + '/api';


interface VerplegingInfo {
  naam: string;
  uren: string;
  vroegeCount?: number;
  lateCount?: number;
  toeren: {
    [toerNaam: string]: {
      dagen: string[];
      type: 'vroege' | 'late';
    };
  };
}

const VerplegingOverzicht: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [verpleegkundigen, setVerpleegkundigen] = useState<VerplegingInfo[]>([]);
  const [toeren, setToeren] = useState<any[]>([]);
  const [publicationStatus, setPublicationStatus] = useState<{ published: boolean; updatedAt: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12

  const normalizeName = (value?: string | null) => (value || '').trim().toLowerCase();

  const buildDisplayName = (n: any) => {
    const fromApi = (n.naam || n.name || n.fullName || '').toString().trim();
    if (fromApi) return fromApi;

    const profileName = `${n.first_name || ''} ${n.last_name || ''}`.trim();
    if (profileName) return profileName;

    if (n.short) return n.short;
    if (n.email) return n.email.split('@')[0];
    return 'Onbekend';
  };

  const buildUrenText = (urenValue: any, vroegeCount: number, lateCount: number) => {
  const urenStr = typeof urenValue === 'string' ? urenValue.trim() : '';

  // Als de API al een string geeft, gewoon teruggeven (maar geen linebreaks meer)
  if (urenStr) {
    return urenStr.replace(/\n/g, ' / ');
  }

  // Anders zelf opbouwen → altijd één lijn
  return `${vroegeCount} vroege / ${lateCount} late`;
};

  const countUniqueDays = (toeren: any) => {
    const uniqueDays = new Set<string>();
    
    Object.values(toeren || {}).forEach((tour: any) => {
      if (tour.dagen && Array.isArray(tour.dagen)) {
        tour.dagen.forEach((day: string) => {
          uniqueDays.add(day);
        });
      }
    });
    
    return uniqueDays.size;
  };


  useEffect(() => {
    document.title = 'Verpleging Overzicht - Thuisverpleging Portaal';
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, selectedYear, selectedMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setError('Geen toegangstoken gevonden');
        return;
      }

      // Fetch data using working API endpoint temporarily
      const [nursesResponse, toursResponse, publicationResponse] = await Promise.all([
        fetch(`${API_BASE}/nurses?year=${selectedYear}&month=${selectedMonth}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE}/tours?year=${selectedYear}&month=${selectedMonth}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE}/roster-publication/${selectedYear}/${selectedMonth}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (!nursesResponse.ok || !toursResponse.ok) {
        throw new Error('Kon data niet ophalen van de server');
      }

      const nursesData = await nursesResponse.json();
      const toursData = await toursResponse.json();
      if (publicationResponse.ok) {
        const publicationData = await publicationResponse.json();
        setPublicationStatus({
          published: !!publicationData.published,
          updatedAt: publicationData.updatedAt || null
        });
      } else {
        setPublicationStatus({
          published: true,
          updatedAt: null
        });
      }

      console.log('API Nurses data:', nursesData);
      console.log('API Tours data:', toursData);

      // Set the actual API data - console shows 77 nurses and 39 tours!
      console.log(`Setting ${nursesData?.length || 0} nurses and ${toursData?.length || 0} tours from API`);
      // Map tour assignments by nurse name so we can display day/shift per nurse
      const assignmentsByNurse: Record<string, any> = {};
      const countsByNurse: Record<string, { vroege: number; late: number }> = {};

      (toursData || []).forEach((tour: any) => {
        const shiftType: 'vroege' | 'late' = tour.type === 'late' ? 'late' : 'vroege';

        Object.entries(tour.dagen || {}).forEach(([dayKey, nurseName]) => {
          if (!nurseName) return;

          const normalized = normalizeName(String(nurseName));
          if (!normalized) return;

          if (!assignmentsByNurse[normalized]) {
            assignmentsByNurse[normalized] = {};
            countsByNurse[normalized] = { vroege: 0, late: 0 };
          }

          if (!assignmentsByNurse[normalized][tour.naam]) {
            assignmentsByNurse[normalized][tour.naam] = { dagen: [], type: shiftType };
          }

          assignmentsByNurse[normalized][tour.naam].dagen.push(String(dayKey).padStart(2, '0'));
          assignmentsByNurse[normalized][tour.naam].type = shiftType;
          countsByNurse[normalized][shiftType] += 1;
        });
      });

      const formattedNurses = (nursesData || []).map((n: any) => {
        const naam = buildDisplayName(n);
        const normalized = normalizeName(naam || n.short || n.email);
        const nurseAssignments = (normalized && assignmentsByNurse[normalized]) || {};
        const assignmentCounts = (normalized && countsByNurse[normalized]) || undefined;
        const apiVroege = Number(n.vroegeCount ?? n.vroegdiensten ?? 0);
        const apiLate = Number(n.lateCount ?? n.latediensten ?? 0);
        const vroegeCount = assignmentCounts?.vroege ?? apiVroege;
        const lateCount = assignmentCounts?.late ?? apiLate;

        return {
          ...n,
          naam,
          toeren: Object.fromEntries(
            Object.entries(nurseAssignments).map(([tourNaam, info]: any) => [
              tourNaam,
              {
                ...info,
                dagen: Array.from(new Set(info.dagen)).sort()
              }
            ])
          ),
          vroegeCount,
          lateCount,
          uren: buildUrenText(n.uren, vroegeCount, lateCount)
        };
      });

      setVerpleegkundigen(formattedNurses);

      setToeren(toursData || []);
      setError(null);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(`API fout: ${err instanceof Error ? err.message : 'Er ging iets mis bij het ophalen van de gegevens'}`);
      
      // Don't clear data on error - keep any successfully loaded data
      // setVerpleegkundigen([]);
      // setToeren([]);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentMonthName = () => {
  const today = new Date();
  const monthNames = [
    'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
  ];

  const year = today.getFullYear();
  const monthName = monthNames[today.getMonth()];

  return `${monthName} ${year}`;
};


  const getDaysInMonth = () => {
    const year = selectedYear;
    const month = selectedMonth - 1; // JS: 0-based

    const lastDay = new Date(year, month + 1, 0).getDate();

    return Array.from({ length: lastDay }, (_, i) =>
      String(i + 1).padStart(2, '0')
    );
  };


  const daysInMonth = getDaysInMonth();

  // Determine view based on user role
  const isAdminView = user?.role === 'admin';
  const showNotPublishedNotice = publicationStatus?.published === false && !isAdminView;

  const filteredVerpleegkundigen = verpleegkundigen.filter(verpleegkundige =>
    verpleegkundige.naam.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredToeren = toeren.filter(toer =>
    toer.naam.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDataForDay = (verpleegkundige: VerplegingInfo, day: string, shiftType: "vroege" | "late") => {
  for (const [toerNaam, toerInfo] of Object.entries(verpleegkundige.toeren || {})) {
    const dagen = toerInfo.dagen.map(d => d.padStart(2, "0"));

    if (dagen.includes(day) && toerInfo.type === shiftType)
      return toerNaam;
  }

  return "-";
};



  const getTourAssignmentForDay = (toer: any, day: string) => {
    const dayNum = parseInt(day, 10);
    const assignment = toer.dagen[dayNum];
    return (assignment !== undefined && assignment !== null && assignment.trim() !== '')
      ? assignment
      : '-';
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        <Sidebar open={true} onClose={() => {}} />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <CircularProgress sx={{ color: '#4aa8b4' }} />
          <Typography variant="h6">Laden van verpleegkundigen data...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar open={true} onClose={() => {}} />

      <Box component="main" sx={{
        flexGrow: 1,
        p: 3,
        marginTop: { xs: '60px', md: 0 },
        overflowX: 'hidden',
        overflowY: 'auto',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Container maxWidth="xl" sx={{
          paddingRight: 0,
          width: '100%',
          maxWidth: 'none'
        }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#4aa8b4', mb: 2 }}>
            Verpleging Overzicht – {new Date(selectedYear, selectedMonth - 1).toLocaleString('nl-BE', {
              month: 'long',
              year: 'numeric'
            })}
            </Typography>
                    {error && (
                      <Box sx={{ mb: 2, p: 2, backgroundColor: '#ffebee', borderRadius: 1 }}>
                        <Typography color="error" variant="body2">
                          {error} (Toont standaard data)
                        </Typography>
                      </Box>
                    )}

            <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              {/* Titel */}
              <Typography variant="h6" sx={{ mr: 1 }}>
                {isAdminView
                  ? `Verpleegkundigen (${filteredVerpleegkundigen.length}/${verpleegkundigen.length})`
                  : `Toeren (${filteredToeren.length}/${toeren.length})`}
              </Typography>

              {/* Zoek */}
              <TextField
                size="small"
                placeholder={isAdminView ? "Zoek verpleegkundige..." : "Zoek toer..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 260 }}
              />

              {/* duw rechts */}
              <Box sx={{ flexGrow: 1 }} />

              {/* maand terug */}
              <IconButton
                onClick={() => {
                  setSelectedMonth((prev: number) => {
                    if (prev === 1) {
                      setSelectedYear((y: number) => y - 1);
                      return 12;
                    }
                    return prev - 1;
                  });
                }}
                sx={{ color: '#4aa8b4' }}
              >
                ◀
              </IconButton>

              {/* jaar dropdown */}
              <TextField
                select
                size="small"
                value={selectedYear}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSelectedYear(Number(e.target.value))
                }
                sx={{ width: 100, backgroundColor: 'white' }}
              >
                {[selectedYear - 1, selectedYear, selectedYear + 1].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>

              {/* maand vooruit */}
              <IconButton
                onClick={() => {
                  setSelectedMonth((prev: number) => {
                    if (prev === 12) {
                      setSelectedYear((y: number) => y + 1);
                      return 1;
                    }
                    return prev + 1;
                  });
                }}
                sx={{ color: '#4aa8b4' }}
              >
                ▶
              </IconButton>

              {/* label */}
              <Typography sx={{ fontWeight: 'bold', color: '#4aa8b4' }}>
                {new Date(selectedYear, selectedMonth - 1).toLocaleString('nl-BE', {
                  month: 'long',
                  year: 'numeric'
                })}
              </Typography>
            </Box>
          </Paper>

          {showNotPublishedNotice && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3cd', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#856404' }}>
                Planning voor deze maand is nog niet gepubliceerd.
              </Typography>
            </Box>
          )}

          {isAdminView ? (
            // Admin view: Show nurses as columns, shifts as sub-columns
            <TableContainer component={Paper} sx={{
              maxWidth: '100%',
              overflowX: 'auto',
              width: 'fit-content'
            }}>
              <Table size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#4aa8b4',
                        color: 'white',
                        minWidth: filteredVerpleegkundigen.length <= 2 ? '40px' : '60px',
                        maxWidth: filteredVerpleegkundigen.length <= 2 ? '40px' : '60px',
                        width: filteredVerpleegkundigen.length <= 2 ? '40px' : '60px',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        textAlign: 'center'
                      }}
                    >

                    </TableCell>
                      {filteredVerpleegkundigen.map((verpleegkundige, index) => {
                        return (
                          <TableCell
                            key={index}
                            colSpan={2}
                            sx={{
                              fontWeight: 'bold',
                              backgroundColor: '#4aa8b4',
                              color: 'white',
                              textAlign: 'center',
                              width: '140px',
                              minWidth: '140px',
                              fontSize: '0.75rem',
                              padding: '8px 6px'
                            }}
                          >
                            <Box>
                              {/* Naam */}
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 'bold', color: 'white', fontSize: '0.9rem' }}
                              >
                                {verpleegkundige.naam}
                              </Typography>

                              {/* Uren totaal */}
                              <Typography
                                variant="caption"
                                sx={{ fontSize: '0.65rem', color: '#e0e0e0', display: 'block' }}
                              >
                                {(countUniqueDays(verpleegkundige.toeren) * 7.6).toFixed(1)} uren
                              </Typography>
                              {/* Vroege / Late */}
                              <Typography
                                variant="caption"
                                sx={{ fontSize: '0.65rem', color: '#e0e0e0', display: 'block' }}
                              >
                                {`${verpleegkundige.vroegeCount ?? 0} vroege / ${verpleegkundige.lateCount ?? 0} late`}
                              </Typography>
                            </Box>
                          </TableCell>
                        );
                      })}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#4aa8b4',
                        color: 'white',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        textAlign: 'center',
                        width: filteredVerpleegkundigen.length <= 2 ? '40px' : '60px',
                        minWidth: filteredVerpleegkundigen.length <= 2 ? '40px' : '60px',
                        maxWidth: filteredVerpleegkundigen.length <= 2 ? '40px' : '60px'
                      }}
                    >

                    </TableCell>
                    {filteredVerpleegkundigen.map((verpleegkundige, index) => (
                      <React.Fragment key={index}>
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor: '#4aa8b4',
                            color: 'white',
                            textAlign: 'center',
                            fontSize: '0.75rem',
                            width: '70px',
                            minWidth: '70px',
                            maxWidth: '70px',
                            padding: '4px 3px'
                          }}
                        >
                          Vroege
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor: '#4aa8b4',
                            color: 'white',
                            textAlign: 'center',
                            fontSize: '0.75rem',
                            width: '70px',
                            minWidth: '70px',
                            maxWidth: '70px',
                            padding: '4px 3px'
                          }}
                        >
                          Late
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {daysInMonth.map((day) => (
                    <TableRow
                      key={day}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#e3f2fd' }
                      }}
                    >
                      <TableCell
                        key={day}
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: '#4aa8b4',
                          color: 'white',
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          textAlign: 'center',
                          width: filteredVerpleegkundigen.length <= 2 ? '40px' : '60px',
                          minWidth: filteredVerpleegkundigen.length <= 2 ? '40px' : '60px',
                          maxWidth: filteredVerpleegkundigen.length <= 2 ? '40px' : '60px',
                          fontSize: filteredVerpleegkundigen.length <= 2 ? '0.7rem' : '0.75rem'
                        }}
                      >
                        {day}
                      </TableCell>
                      {filteredVerpleegkundigen.map((verpleegkundige, index) => (
                        <React.Fragment key={index}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              padding: '4px 3px',
                              width: '70px',
                              minWidth: '70px',
                              maxWidth: '70px',
                              fontSize: '0.75rem'
                            }}
                          >
                            {getDataForDay(verpleegkundige, day, 'vroege')}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              padding: '4px 3px',
                              width: '70px',
                              minWidth: '70px',
                              maxWidth: '70px',
                              fontSize: '0.75rem',
                              borderRight: '3px solid #4aa8b4'
                            }}
                          >
                            {getDataForDay(verpleegkundige, day, 'late')}
                          </TableCell>
                        </React.Fragment>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            // VPK view: Show tours as columns, nurse assignments as data
            <TableContainer component={Paper} sx={{
              maxWidth: '100%',
              overflowX: 'auto',
              width: 'fit-content'
            }}>
              <Table size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#4aa8b4',
                        color: 'white',
                        width: { xs: '80px', sm: '100px', md: '120px' },
                        minWidth: { xs: '80px', sm: '100px', md: '120px' },
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        textAlign: 'center'
                      }}
                    >
                      Dag
                    </TableCell>
                    {filteredToeren.map((toer, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: '#4aa8b4',
                          color: 'white',
                          textAlign: 'center',
                          minWidth: { xs: '120px', sm: '140px', md: '160px' },
                          fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                          p: { xs: 0.5, sm: 1 }
                        }}
                      >
                        {toer.naam}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {daysInMonth.map((day) => {
                    const dag = parseInt(day, 10);
                    return (
                      <TableRow
                        key={day}
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                          '&:hover': { backgroundColor: '#e3f2fd' }
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            color: '#ffffff',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: '#4aa8b4',
                            zIndex: 1,
                            textAlign: 'center',
                            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }
                          }}
                        >
                          {String(dag).padStart(2, '0')}
                        </TableCell>
                        {filteredToeren.map((toer, toerIndex) => {
                          const assignment = getTourAssignmentForDay(toer, String(dag).padStart(2, '0'));
                          return (
                            <TableCell
                              key={toerIndex}
                              sx={{
                                textAlign: 'center',
                                p: { xs: 0.5, sm: 1 },
                                borderLeft: toerIndex === 0 ? 'none' : '1px solid #e0e0e0'
                              }}
                            >
                              {assignment !== '-' ? (
                                <Chip
                                  label={assignment}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    fontSize: '0.7rem',
                                    height: '20px'
                                  }}
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {isAdminView ? (
                searchTerm ? (
                  `Toont ${filteredVerpleegkundigen.length} van ${verpleegkundigen.length} verpleegkundigen - gefilterd op "${searchTerm}"`
                ) : (
                  `Toont alle ${verpleegkundigen.length} verpleegkundigen met hun toeren voor ${new Date(selectedYear, selectedMonth - 1).toLocaleString('nl-BE', {month: 'long',year: 'numeric'}).toLowerCase()}`
                )
              ) : (
                searchTerm ? (
                  `Toont ${filteredToeren.length} van ${toeren.length} toeren - gefilterd op "${searchTerm}"`
                ) : (
                  `Toont alle ${toeren.length} toeren met toegewezen verpleegkundigen voor ${new Date(selectedYear, selectedMonth - 1).toLocaleString('nl-BE', {month: 'long',year: 'numeric'}).toLowerCase()}`
                )
              )}
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default VerplegingOverzicht;
